import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from './layouts/AppShell';
import { AuthGuard } from './layouts/AuthGuard';
import { LandingLayout } from './layouts/LandingLayout';

export const router = createBrowserRouter([
  // ── 랜딩 (공개) ─────────────────────────────
  {
    path: '/',
    element: <LandingLayout />,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('./routes/landing/index')).default }),
      },
      {
        path: 'pricing',
        lazy: async () => ({ Component: (await import('./routes/landing/pricing')).default }),
      },
      {
        path: 'terms',
        lazy: async () => ({ Component: (await import('./routes/landing/terms')).default }),
      },
      {
        path: 'privacy',
        lazy: async () => ({ Component: (await import('./routes/landing/privacy')).default }),
      },
      {
        path: 'faq',
        lazy: async () => ({ Component: (await import('./routes/landing/faq')).default }),
      },
    ],
  },

  // ── 앱 (PWA Scope) ───────────────────────────
  {
    path: '/app',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/app/home" replace /> },
      {
        path: 'login',
        lazy: async () => ({ Component: (await import('./routes/login')).default }),
      },
      {
        path: 'onboarding',
        lazy: async () => ({ Component: (await import('./routes/onboarding')).default }),
      },
      {
        element: <AuthGuard />,
        children: [
          {
            path: 'home',
            lazy: async () => ({ Component: (await import('./routes/home')).default }),
          },

          // Stage Wizard (v0.9)
          {
            path: 'studio',
            children: [
              { index: true, element: <Navigate to="/app/studio/source" replace /> },
              {
                path: 'source',
                lazy: async () => ({
                  Component: (await import('./routes/studio/source')).default,
                }),
              },
              {
                path: 'face-complete/:srcId',
                lazy: async () => ({
                  Component: (await import('./routes/studio/face-complete')).default,
                }),
              },
              {
                path: 'style/:srcId',
                lazy: async () => ({
                  Component: (await import('./routes/studio/style')).default,
                }),
              },
              {
                path: 'generating/:jobId',
                lazy: async () => ({
                  Component: (await import('./routes/studio/generating')).default,
                }),
              },
              {
                path: 'retouch/:genId',
                lazy: async () => ({
                  Component: (await import('./routes/studio/retouch')).default,
                }),
              },
              {
                path: 'output/:genId',
                lazy: async () => ({
                  Component: (await import('./routes/studio/output')).default,
                }),
              },
            ],
          },

          // v0.8 legacy routes → Stage redirect
          { path: 'studio/new', element: <Navigate to="/app/studio/source" replace /> },
          { path: 'studio/new/catalog', element: <Navigate to="/app/studio/source" replace /> },
          { path: 'studio/new/upload', element: <Navigate to="/app/studio/source" replace /> },
          { path: 'studio/new/prompt', element: <Navigate to="/app/studio/source" replace /> },
          { path: 'studio/new/reel', element: <Navigate to="/app/studio/source" replace /> },
          { path: 'studio/preset/:id', element: <Navigate to="/app/studio/source" replace /> },
          { path: 'result/:id', element: <RedirectToOutput /> },
          { path: 'result/:id/export', element: <RedirectToOutput /> },
          { path: 'result/:id/caption', element: <RedirectToOutput /> },
          { path: 'result/:id/retouch', element: <RedirectToOutput /> },

          {
            path: 'gallery',
            lazy: async () => ({ Component: (await import('./routes/gallery')).default }),
          },
          {
            path: 'me',
            lazy: async () => ({ Component: (await import('./routes/me/index')).default }),
          },
          {
            path: 'me/billing',
            lazy: async () => ({ Component: (await import('./routes/me/billing')).default }),
          },
          {
            path: 'me/credits',
            lazy: async () => ({ Component: (await import('./routes/me/credits')).default }),
          },
          {
            path: 'referral',
            lazy: async () => ({ Component: (await import('./routes/referral')).default }),
          },
        ],
      },
    ],
  },
]);

function RedirectToOutput() {
  const path = window.location.pathname;
  const match = path.match(/\/app\/result\/([^/]+)/);
  const id = match?.[1] ?? 'gen-001';
  return <Navigate to={`/app/studio/output/${id}`} replace />;
}
